import {
  AppBar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Typography,
} from '@mui/material';
import { type MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { AppHeaderAccountDialog } from './components/AppHeaderAccountDialog';
import { AppHeaderLogoutConfirmDialog } from './components/AppHeaderLogoutConfirmDialog';
import { AppHeaderResetPasswordDialog } from './components/AppHeaderResetPasswordDialog';
import logo from './images/verantos-logo-reversed.svg';
import { useAppHeader } from './useAppHeader';
import { useAuth } from '../../core/auth';

export interface AppHeaderProps {
  /**
   * @prop {string} id - The id of the app header
   * @default 'appHeader
   */
  id?: string;

  /**
   * @prop {string} appSectionName - the name of the application section
   * @default ''
   */
  appSectionName?: string;

  /**
   * @prop {string} platformNameMessageId - the intl message ID for the platform name (e.g., "appHeader.platform.name", "appHeader.platform.name.internal")
   */
  platformNameMessageId: string;

  /**
   *
   * @prop {function} onLogoClick - a function that can called when the user click on the logo
   */
  onLogoClick?: MouseEventHandler<HTMLAnchorElement>;
}

export function AppHeader({
  id = 'appHeader',
  appSectionName = '',
  platformNameMessageId,
  onLogoClick,
}: AppHeaderProps) {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const {
    userMenu,
    onClickUserMenu,
    onCloseUserMenu,
    onClickUserMenuItem,
    showAccoutDialog,
    onCloseAccoutDialog,
    showResetPasswordDialog,
    onCloseResetPasswordDialog,
    showLogoutConfirmDialog,
    onCloseConfirmLogoutDialog,
  } = useAppHeader();

  const appHeaderStyle = {
    '[data-initials]::before': {
      backgroundColor: '#fff',
      opacity: 1,
      content: 'attr(data-initials)',
      display: 'inline-block',
      fontWeight: 'bold',
      borderRadius: '50%',
      verticalAlign: 'middle',
      //marginRight: '0.5em',
      width: '24px',
      height: '24px',
      fontSize: '1.2rem',
      lineHeight: '24px',
      textAlign: 'center',
    },
  };

  // Styled MenuItem
  const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(204,240, 249, 0.60)',
    },
  }));

  const CustomDivider = styled(Divider)(({ theme }) => ({
    '&.MuiDivider-root': {
      margin: 0,
      borderColor: theme.palette.divider,
      borderWidth: '1px',
    },
  }));

  return (
    <Box sx={appHeaderStyle} data-testid={id}>
      <AppBar position="static" data-testid={`${id}-AppBar`} sx={{ boxShadow: 'none', px: 3 }}>
        <Toolbar data-testid={`${id}-Toolbar`}>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Box>
              <Link
                style={{ display: 'block' }}
                to="/dashboard"
                onClick={onLogoClick}
                data-testid={`${id}-Link`}
              >
                <img
                  src={logo}
                  alt={intl.formatMessage({ id: 'appHeader.verantosLogo.alt' })}
                  style={{ width: '144px', height: '24px', marginTop: '5px' }}
                  data-testid={`${id}-verantos-logo`}
                />
              </Link>
            </Box>
            <Box
              sx={(theme) => ({
                height: theme.spacing(6),
                mx: theme.spacing(6),
                border: '1px solid',
                borderColor: theme.palette.divider,
              })}
            />
            <Box sx={{ display: 'flex' }}>
              <Typography
                variant="body1"
                sx={{ color: (theme) => `${theme?.palette?.white?.main}`, fontWeight: 700 }}
                data-testid={`${id}-platform-name`}
              >
                {intl.formatMessage({ id: platformNameMessageId })}
              </Typography>
              {appSectionName && (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      color: (theme) => `${theme.palette.white.main}`,
                      fontWeight: 700,
                      px: '10px',
                    }}
                    data-testid={`${id}-platform-name-separator`}
                  >
                    /
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{ color: (theme) => `${theme.palette.white.main}` }}
                    data-testid={`${id}-section-name`}
                  >
                    {appSectionName}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Box>
            <IconButton
              onClick={onClickUserMenu}
              sx={{ fontSize: 0, padding: '2px' }}
              data-testid={`${id}-user-button`}
            >
              <Box data-initials={user.userInitials} data-testid={`${id}-user-button-icon`} />
            </IconButton>
            <Menu
              id="appHeaderUserMenu"
              data-testid={`${id}-user-menu`}
              anchorEl={userMenu}
              anchorOrigin={{
                vertical: 32, // bottom
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(userMenu)}
              onClose={onCloseUserMenu}
              sx={{
                '& .MuiPaper-root': {
                  minWidth: '200px', // Set minimum width
                },
              }}
            >
              <CustomMenuItem
                key="appHeaderUserMenuAccountSetting"
                onClick={() => onClickUserMenuItem('accountSetting')}
                data-testid={`${id}-user-menu-item-account-setting`}
              >
                {intl.formatMessage({ id: 'appHeader.userMenu.accountSettings' })}
              </CustomMenuItem>
              {/*
              // TODO: Uncomment this when the help and support page is ready
               <CustomMenuItem
                key="appHeaderUserMenuHelpSupport"
                onClick={() => onClickUserMenuItem('help&support')}
                data-testid={`${id}-user-menu-item-help-support`}
              >
                {intl.formatMessage({ id: 'appHeader.userMenu.helpSupport' })}
              </CustomMenuItem> */}
              <CustomDivider />
              <CustomMenuItem
                key="appHeaderUserMenuLogout"
                onClick={() => onClickUserMenuItem('logout')}
                data-testid={`${id}-user-menu-item-logout`}
                sx={{ color: '#f14c2e' }}
              >
                {intl.formatMessage({ id: 'appHeader.userMenu.logout' })}
              </CustomMenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box>
        {showAccoutDialog && <AppHeaderAccountDialog onClose={onCloseAccoutDialog} user={user} />}
        {showResetPasswordDialog && (
          <AppHeaderResetPasswordDialog
            onClose={onCloseResetPasswordDialog}
            userId={user.userId}
          ></AppHeaderResetPasswordDialog>
        )}
        {showLogoutConfirmDialog && (
          <AppHeaderLogoutConfirmDialog onClose={onCloseConfirmLogoutDialog} onLogout={logout} />
        )}
      </Box>
    </Box>
  );
}

// export wrapped component by default
export default AppHeader;
